import { apiClient } from '@/config/api';
import { Box, HydraCollection, PaginatedResponse } from '@/types';

export const boxService = {
  // Get all boxes
  async getAllBoxes(): Promise<Box[]> {
    const response = await apiClient.get<HydraCollection<Box>>('/boxes');
    const data = response.data;
    const boxes = data['hydra:member'] || data['member'] || [];
    return boxes;
  },

  // Get boxes by project with pagination and filters
  async getBoxesByProject(
    projectId: number,
    search?: string,
    page: number = 1,
    itemsPerPage: number = 10,
    statusFilter?: 'online' | 'offline',
    typeFilter?: string
  ): Promise<PaginatedResponse<Box>> {
    const params: Record<string, any> = {
      'project': `/api/projects/${projectId}`,
    };

    // Add pagination params only if itemsPerPage is not too large (disable pagination for large requests)
    if (itemsPerPage < 10000) {
      params.page = page;
      params.itemsPerPage = itemsPerPage;
    } else {
      // Disable pagination for large requests
      params.pagination = false;
    }

    // Add search parameter if provided (searches both name and code)
    if (search && search.trim()) {
      params['search'] = search.trim();
    }

    // Add status filter if provided
    if (statusFilter) {
      params['isOnline'] = statusFilter === 'online';
    }

    // Add type filter if provided
    if (typeFilter && typeFilter !== 'all') {
      params['type'] = `/api/box_types/${typeFilter}`;
    }

    const response = await apiClient.get<HydraCollection<Box> | Box[]>('/boxes', {
      params,
    });

    const data = response.data;
    
    // Handle case when pagination is disabled (API returns array directly)
    if (Array.isArray(data)) {
      return {
        data: data,
        totalItems: data.length,
        currentPage: 1,
        itemsPerPage: data.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    // Handle paginated response (HydraCollection format)
    const boxes = data['hydra:member'] || data['member'] || [];
    const totalItems = data['hydra:totalItems'] || data['totalItems'] || 0;
    const view = data['hydra:view'] || data['view'];

    // Calculate pagination info
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const hasNextPage = !!view?.['hydra:next'];
    const hasPreviousPage = !!view?.['hydra:previous'];

    return {
      data: boxes,
      totalItems,
      currentPage: page,
      itemsPerPage,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },

  // Get box with devices and attributes (optimized)
  async getBoxWithDevicesAndAttributes(boxId: number): Promise<Box> {
    const response = await apiClient.get<Box>(`/boxes/${boxId}`, {
      params: {
        'groups[]': ['box:read', 'device:read', 'attribute:read'],
      },
    });
    return response.data;
  },

  // Get box with devices (lazy loading)
  async getBoxWithDevices(boxId: number): Promise<Box> {
    const response = await apiClient.get<Box>(`/boxes/${boxId}`);
    return response.data;
  },

  // Get single box by ID
  async getBox(id: number): Promise<Box> {
    const response = await apiClient.get<Box>(`/boxes/${id}`);
    return response.data;
  },

  // Create box
  async createBox(data: Partial<Box>): Promise<Box> {
    const response = await apiClient.post('/boxes', data);
    return response.data;
  },

  // Update box
  async updateBox(id: number, data: Partial<Box>): Promise<Box> {
    const response = await apiClient.put(`/boxes/${id}`, data);
    return response.data;
  },

  // Delete box
  async deleteBox(id: number): Promise<void> {
    await apiClient.delete(`/boxes/${id}`);
  },

  // Associate box with area
  async associateBoxWithArea(boxId: number, areaId: number): Promise<void> {
    await apiClient.post(`/boxes/${boxId}/areas/${areaId}`);
  },

  // Disassociate box from area
  async disassociateBoxFromArea(boxId: number, areaId: number): Promise<void> {
    await apiClient.delete(`/boxes/${boxId}/areas/${areaId}`);
  },

  // Import box associations from CSV
  async importBoxAssociations(projectId: number, file: File): Promise<{
    success: boolean;
    summary: {
      associated: number;
      skipped: number;
      total_processed: number;
      errors: string[];
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/projects/${projectId}/boxes/association/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
